import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Save, Check, Sparkles, MapPin, X, User, Waves, Wind, ArrowDownUp, Clock, Thermometer, Search, ChevronDown, Crown, ArrowRight, ExternalLink, Mail, Bell } from 'lucide-react';
import PageShell from '../components/PageShell';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../utils/api';


const SURF_LEVELS = ['beginner', 'intermediate', 'advanced', 'pro'];
const WAVE_TYPES = ['Beach break', 'Point break', 'Reef break', 'River mouth', 'Wedge'];
const WIND_OPTIONS = ['Offshore', 'Light onshore', 'Cross-shore', 'No preference'];
const SWELL_DIRS = ['N', 'S', 'W', 'E', 'NW', 'SW', 'NE', 'SE'];
const TIDE_OPTIONS = ['Low', 'Mid', 'High', 'No preference'];
const SESSION_TIMES = ['Dawn patrol', 'Morning', 'Midday', 'Afternoon', 'Sunset', 'No preference'];
const WAVE_HEIGHTS = ['1-2ft', '2-3ft', '3-4ft', '4-6ft', '6-8ft', '8-10ft', '10-12ft', '12ft+'];
const WETSUIT_OPTIONS = ['Boardshorts', 'Rashguard', 'Springsuit', '2mm', '3/2mm', '4/3mm', '5/4mm', '6/5mm', 'Drysuit'];
const ALL_BOARD_TYPES = [
  'Shortboard', 'Longboard', 'Fish', 'Funboard', 'Gun',
  'Mid-Length', 'Foamie', 'Step-Up', 'Hybrid', 'Mini Simmons',
  'Egg', 'Semi-Gun', 'Bodyboard', 'SUP', 'Skimboard',
];

function Section({ icon: Icon, title, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay * 0.06 }}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="w-3.5 h-3.5 text-primary/50" />
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-medium">{title}</p>
      </div>
      {children}
    </motion.div>
  );
}

function PillSelect({ options, value, onChange, multi = false, testPrefix = 'pref' }) {
  const isSelected = (opt) => multi ? (value || []).includes(opt) : value === opt;
  const handleClick = (opt) => {
    if (multi) {
      onChange(isSelected(opt) ? (value || []).filter(v => v !== opt) : [...(value || []), opt]);
    } else {
      onChange(value === opt ? '' : opt);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => (
        <button
          key={opt}
          data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[\s\/]/g, '-')}`}
          onClick={() => handleClick(opt)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            isSelected(opt)
              ? 'bg-primary/20 text-primary border border-primary/30 shadow-sm shadow-primary/10'
              : 'bg-white/[0.04] text-muted-foreground/70 border border-white/8 hover:border-white/15 hover:text-white'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function SpotSearchSelect({ value, onChange, placeholder, testId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchSpots = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/spots/spot-names?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.spots || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSpots(val), 250);
  };

  const selectSpot = (name) => {
    onChange(name);
    setQuery('');
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
        <input
          data-testid={testId}
          type="text"
          value={open ? query : value || ''}
          onFocus={() => { setQuery(value || ''); setOpen(true); if (value) searchSpots(value); }}
          onChange={handleInput}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
        />
        {value && !open && (
          <button
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full"
          >
            <X className="w-3 h-3 text-muted-foreground/50" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-20 top-full mt-1 w-full bg-bg-paper border border-white/10 rounded-xl shadow-xl max-h-48 overflow-y-auto"
          >
            {results.map((s, i) => (
              <button
                key={`${s.name}-${i}`}
                data-testid={`spot-option-${i}`}
                onClick={() => selectSpot(s.name)}
                className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors flex items-center gap-2 border-b border-white/[0.03] last:border-0"
              >
                <MapPin className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs truncate">{s.name}</p>
                  {s.region && <p className="text-[9px] text-muted-foreground/40">{s.region}</p>}
                </div>
              </button>
            ))}
            {loading && (
              <div className="px-3 py-2 text-[10px] text-muted-foreground/40 text-center">searching...</div>
            )}
          </motion.div>
        )}
        {open && query.length >= 2 && results.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute z-20 top-full mt-1 w-full bg-bg-paper border border-white/10 rounded-xl shadow-xl px-3 py-3 text-center"
          >
            <p className="text-[10px] text-muted-foreground/40">no spots found for "{query}"</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MultiSpotSelect({ spots, onChange, testId }) {
  const addSpot = (name) => {
    if (name && !spots.includes(name)) {
      onChange([...spots, name]);
    }
  };
  const removeSpot = (name) => {
    onChange(spots.filter(s => s !== name));
  };

  return (
    <div>
      <SpotSearchSelect
        value=""
        onChange={addSpot}
        placeholder="search & add spots..."
        testId={testId}
      />
      {spots.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {spots.map(spot => (
            <span key={spot} className="bg-primary/10 text-primary border border-primary/20 rounded-full px-2.5 py-1 text-[10px] inline-flex items-center gap-1">
              {spot}
              <button data-testid={`remove-fav-${spot.replace(/\s/g, '-')}`} onClick={() => removeSpot(spot)} className="text-primary/60 hover:text-primary">
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SubscriptionSection() {
  const { isPro, subscription } = useSubscription();
  const navigate = useNavigate();

  if (isPro) {
    return (
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Crown className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <p className="text-sm font-heading font-bold text-white">Swell Pro</p>
              <p className="text-[10px] text-muted-foreground">
                {subscription.interval === 'year' ? 'Annual' : 'Monthly'} plan
                {subscription.cancel_at_period_end ? ' — cancels at period end' : ''}
              </p>
            </div>
          </div>
          <button
            data-testid="manage-sub-btn"
            onClick={() => navigate('/billing')}
            className="flex items-center gap-1 text-[10px] text-amber-400 hover:text-amber-300 font-medium transition-colors"
          >
            Manage <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/15 rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-amber-500/10 rounded-xl flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-heading font-bold text-white">Go Pro</p>
            <p className="text-[10px] text-muted-foreground">Unlock unlimited features</p>
          </div>
        </div>
        <button
          data-testid="go-pro-btn"
          onClick={() => navigate('/billing')}
          className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg hover:brightness-110 transition-all"
        >
          <Sparkles className="w-3 h-3" /> Upgrade <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
}

export default function Preferences() {
  const [surfLevel, setSurfLevel] = useState('beginner');
  const [homeBreak, setHomeBreak] = useState('');
  const [prefs, setPrefs] = useState({
    preferred_wave_types: [],
    preferred_wind: '',
    preferred_swell_direction: '',
    min_wave_height: '',
    max_wave_height: '',
    preferred_tide: '',
    preferred_session_time: '',
    favorite_spots: [],
    wetsuit_thickness: '',
    preferred_board_types: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [garageTypes, setGarageTypes] = useState([]);
  const [garageBoards, setGarageBoards] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [showRecommended, setShowRecommended] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, prefsRes, boardsRes] = await Promise.all([
          authFetch(`${API}/api/profile`),
          authFetch(`${API}/api/preferences`),
          authFetch(`${API}/api/boards/my-board-types`),
        ]);
        if (profileRes.ok) {
          const p = await profileRes.json();
          setSurfLevel(p.surf_level || 'beginner');
          setHomeBreak(p.home_break || '');
        }
        if (prefsRes.ok) {
          const data = await prefsRes.json();
          setPrefs(prev => ({ ...prev, ...data }));
        }
        if (boardsRes.ok) {
          const data = await boardsRes.json();
          setGarageTypes(data.board_types || []);
          setGarageBoards(data.boards || []);
        }
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await Promise.all([
        authFetch(`${API}/api/profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ surf_level: surfLevel, home_break: homeBreak }),
        }),
        authFetch(`${API}/api/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(prefs),
        }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadRecommended();
    } catch {} finally { setSaving(false); }
  };

  const loadRecommended = async () => {
    try {
      const res = await authFetch(`${API}/api/spots/recommended`);
      if (res.ok) {
        const data = await res.json();
        setRecommended(data.recommended || []);
      }
    } catch {}
  };

  useEffect(() => {
    if (!loading) loadRecommended();
  }, [loading]);

  // Board types: merge garage types with selections
  const garageTypesNotSelected = garageTypes.filter(t => !(prefs.preferred_board_types || []).includes(t));
  const topRecs = recommended.filter(r => r.match_score >= 60).slice(0, 5);

  if (loading) {
    return (
      <PageShell title="Preferences">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Preferences">
      <div className="p-5 space-y-6" data-testid="preferences-page">
        {/* Subscription Status */}
        <SubscriptionSection />

        {/* Swell hint */}
        <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            These power <span className="text-primary font-medium">Swell</span>'s recommendations and spot matching. You can also update preferences by chatting with Swell.
          </p>
        </div>

        {/* ── About You ──────────────────────────────────────────── */}
        <Section icon={User} title="About You" delay={0}>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Surf Level</label>
              <div className="grid grid-cols-2 gap-2">
                {SURF_LEVELS.map(level => (
                  <button
                    key={level}
                    data-testid={`pref-level-${level}`}
                    onClick={() => setSurfLevel(level)}
                    className={`px-3 py-2.5 rounded-xl text-xs font-medium capitalize transition-all ${
                      surfLevel === level
                        ? 'bg-primary/20 text-primary border border-primary/30 shadow-sm shadow-primary/10'
                        : 'bg-white/[0.04] border border-white/8 text-muted-foreground/70 hover:border-white/15'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Home Break</label>
              <SpotSearchSelect
                value={homeBreak}
                onChange={setHomeBreak}
                placeholder="search for your go-to spot..."
                testId="pref-home-break"
              />
            </div>
          </div>
        </Section>

        {/* ── Wave Preferences ───────────────────────────────────── */}
        <Section icon={Waves} title="Wave Preferences" delay={1}>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Wave Types</label>
              <PillSelect
                options={WAVE_TYPES}
                value={prefs.preferred_wave_types}
                onChange={v => setPrefs(p => ({ ...p, preferred_wave_types: v }))}
                multi
                testPrefix="pref-wave"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Height Range</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[8px] text-muted-foreground/40 mb-1">Min</p>
                  <div className="flex flex-wrap gap-1">
                    {WAVE_HEIGHTS.slice(0, 4).map(h => (
                      <button
                        key={h}
                        data-testid={`pref-min-${h}`}
                        onClick={() => setPrefs(p => ({ ...p, min_wave_height: p.min_wave_height === h ? '' : h }))}
                        className={`px-2 py-1 rounded-lg text-[10px] transition-all ${
                          prefs.min_wave_height === h
                            ? 'bg-primary/20 text-primary border border-primary/30'
                            : 'bg-white/[0.04] text-muted-foreground/60 border border-white/8 hover:border-white/15'
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[8px] text-muted-foreground/40 mb-1">Max</p>
                  <div className="flex flex-wrap gap-1">
                    {WAVE_HEIGHTS.slice(2).map(h => (
                      <button
                        key={h}
                        data-testid={`pref-max-${h}`}
                        onClick={() => setPrefs(p => ({ ...p, max_wave_height: p.max_wave_height === h ? '' : h }))}
                        className={`px-2 py-1 rounded-lg text-[10px] transition-all ${
                          prefs.max_wave_height === h
                            ? 'bg-primary/20 text-primary border border-primary/30'
                            : 'bg-white/[0.04] text-muted-foreground/60 border border-white/8 hover:border-white/15'
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Conditions ─────────────────────────────────────────── */}
        <Section icon={Wind} title="Conditions" delay={2}>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Preferred Wind</label>
              <PillSelect
                options={WIND_OPTIONS}
                value={prefs.preferred_wind}
                onChange={v => setPrefs(p => ({ ...p, preferred_wind: v }))}
                testPrefix="pref-wind"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Preferred Swell</label>
              <PillSelect
                options={SWELL_DIRS}
                value={prefs.preferred_swell_direction}
                onChange={v => setPrefs(p => ({ ...p, preferred_swell_direction: v }))}
                testPrefix="pref-swell"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Preferred Tide</label>
              <PillSelect
                options={TIDE_OPTIONS}
                value={prefs.preferred_tide}
                onChange={v => setPrefs(p => ({ ...p, preferred_tide: v }))}
                testPrefix="pref-tide"
              />
            </div>
          </div>
        </Section>

        {/* ── Session ────────────────────────────────────────────── */}
        <Section icon={Clock} title="Session" delay={3}>
          <div>
            <label className="text-[10px] text-muted-foreground/60 block mb-2">Preferred Time</label>
            <PillSelect
              options={SESSION_TIMES}
              value={prefs.preferred_session_time}
              onChange={v => setPrefs(p => ({ ...p, preferred_session_time: v }))}
              testPrefix="pref-time"
            />
          </div>
        </Section>

        {/* ── Gear ───────────────────────────────────────────────── */}
        <Section icon={Thermometer} title="Gear" delay={4}>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Wetsuit</label>
              <PillSelect
                options={WETSUIT_OPTIONS}
                value={prefs.wetsuit_thickness}
                onChange={v => setPrefs(p => ({ ...p, wetsuit_thickness: v }))}
                testPrefix="pref-wetsuit"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/60 block mb-2">Board Types</label>
              {garageTypesNotSelected.length > 0 && (
                <div className="bg-secondary/5 border border-secondary/15 rounded-xl p-2.5 mb-2">
                  <p className="text-[9px] text-secondary/70 mb-1.5">From your Garage:</p>
                  <div className="flex flex-wrap gap-1">
                    {garageTypesNotSelected.map(t => (
                      <button
                        key={t}
                        data-testid={`garage-type-${t.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setPrefs(p => ({ ...p, preferred_board_types: [...(p.preferred_board_types || []), t] }))}
                        className="px-2.5 py-1 rounded-full text-[10px] bg-secondary/15 text-secondary border border-secondary/25 hover:bg-secondary/25 transition-all"
                      >
                        + {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <PillSelect
                options={ALL_BOARD_TYPES}
                value={prefs.preferred_board_types || []}
                onChange={v => setPrefs(p => ({ ...p, preferred_board_types: v }))}
                multi
                testPrefix="pref-board"
              />
              {garageBoards.length > 0 && (
                <div className="mt-2">
                  <p className="text-[8px] text-muted-foreground/30 mb-1">Your quiver:</p>
                  <div className="flex flex-wrap gap-1">
                    {garageBoards.map((b, i) => (
                      <span key={i} className="text-[9px] text-muted-foreground/50 bg-white/[0.03] border border-white/5 rounded-lg px-2 py-0.5">
                        {b.brand} {b.name} ({b.board_type})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* ── Favorite Spots ─────────────────────────────────────── */}
        <Section icon={MapPin} title="Favorite Spots" delay={5}>
          <MultiSpotSelect
            spots={prefs.favorite_spots || []}
            onChange={v => setPrefs(p => ({ ...p, favorite_spots: v }))}
            testId="pref-fav-spot-search"
          />
        </Section>

        {/* Email Notification Preferences */}
        <Section icon={Mail} title="Email Notifications" delay={6}>
          <div className="space-y-2">
            {[
              { key: 'email_welcome', label: 'Welcome email', desc: 'Receive a welcome email when you sign up' },
              { key: 'email_swell_alerts', label: 'Swell alerts', desc: 'Get notified when conditions are good at your starred spots' },
              { key: 'email_crew_invites', label: 'Crew invites', desc: 'Receive emails when someone invites you to their crew' },
              { key: 'email_spot_milestones', label: 'Spot milestones', desc: 'Get notified when your submitted spots earn Community Verified' },
              { key: 'email_trip_reminders', label: 'Trip reminders', desc: 'Receive reminders before your upcoming surf trips' },
              { key: 'email_session_summaries', label: 'Session summaries', desc: 'Weekly summary of your surf sessions and progress' },
            ].map(({ key, label, desc }) => (
              <button
                key={key}
                data-testid={`pref-${key}`}
                onClick={() => setPrefs(p => ({ ...p, [key]: !(p[key] !== false) }))}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-bg-subtle/50 border border-white/5 hover:border-white/10 transition-colors text-left"
              >
                <div className={`w-9 h-5 rounded-full flex-shrink-0 transition-colors relative ${prefs[key] !== false ? 'bg-primary' : 'bg-white/10'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${prefs[key] !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium">{label}</p>
                  <p className="text-[9px] text-muted-foreground/60 mt-0.5">{desc}</p>
                </div>
              </button>
            ))}
          </div>
        </Section>

        {/* Save */}
        <button
          data-testid="pref-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-primary text-primary-foreground hover:opacity-90 font-bold text-sm py-3 rounded-2xl transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saved ? <><Check className="w-4 h-4" /> Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save Preferences</>}
        </button>

        {/* Recommended Spots Preview */}
        {topRecs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ArrowDownUp className="w-3.5 h-3.5 text-primary/50" />
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50">Spots matching your prefs</p>
              </div>
              <button
                data-testid="pref-view-all-recs"
                onClick={() => setShowRecommended(!showRecommended)}
                className="text-[10px] text-primary font-bold hover:opacity-80"
              >
                {showRecommended ? 'Hide' : `View all (${recommended.length})`}
              </button>
            </div>
            <div className="space-y-2">
              {(showRecommended ? recommended.slice(0, 15) : topRecs).map((spot, i) => (
                <div
                  key={`${spot.name}-${i}`}
                  className="bg-bg-subtle/50 border border-white/5 rounded-xl p-3 flex items-center gap-3"
                  data-testid={`rec-spot-${i}`}
                >
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{spot.name}</p>
                    <p className="text-[9px] text-muted-foreground">{spot.region} &middot; {spot.wave_type || spot.difficulty}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="w-8 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${spot.match_score}%` }} />
                    </div>
                    <span className="text-[10px] text-primary font-bold">{spot.match_score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </PageShell>
  );
}
